// We probably want to move and fully extend those types to a separate file
// and import them here, but for now we can just define them inline

import {
  Annotation,
  DebateRound,
  TeamReference,
  TextNode,
  UserReference,
} from '@/types/backend-entities';

interface ProcessedAnnotation {
  numeric: boolean;
  fromOrder: number;
  toOrder: number;
  fromKey: number;
  toKey: number;
  outlineId?: string;
  original: Annotation;
}

interface ProcessedCounterpoint {
  _id: string;
  author: UserReference;
  team: TeamReference;
  annotations: ProcessedAnnotation[];
}

interface AnnotationSpatialIndex {
  numeric: Map<number, ProcessedAnnotation[]>;
  outline: Map<string, Map<number, ProcessedAnnotation[]>>;
}

interface PreparedCounterpoints {
  counterpoints: ProcessedCounterpoint[];
  spatialIndex: AnnotationSpatialIndex;
}

interface ProcessedTextNode extends TextNode {
  numeric: boolean;
  order: number;
  keyNum: number;
  outlineId?: string;
}

/**
 * Converts an array to a keyed object using the specified property as the key
 */
export const toKeyedObj = <T extends Record<string, any>>(
  arr: T[],
  keyProp: keyof T
): Record<string, T> => {
  const out: Record<string, T> = {};
  if (Array.isArray(arr)) {
    for (let i = 0, len = arr.length; i < len; i++) {
      const el = arr[i];
      if (el && el[keyProp] != null) out[String(el[keyProp])] = el;
    }
  }
  return out;
};

/**
 * Pre-processes annotation data for easier processing
 * Returns null if annotation data is invalid or missing required fields
 */
export const prepAnnotation = (ann: Annotation): ProcessedAnnotation | null => {
  if (!ann?.selectedText?.from || !ann?.selectedText?.to) {
    return null;
  }

  const annotationFrom = ann?.selectedText?.from?.nodeOrder;

  const { from, to } = ann.selectedText;

  if (typeof annotationFrom === 'number') {
    return {
      numeric: true,
      fromOrder: from.nodeOrder as number,
      toOrder: to.nodeOrder as number,
      fromKey: +from.nodeKey.split('-')[1],
      toKey: +to.nodeKey.split('-')[1],
      original: ann,
    };
  } else if (typeof annotationFrom === 'string') {
    const [fromId, fromOrd] = (from.nodeOrder as string).split('-');
    const [, toOrd] = (to.nodeOrder as string).split('-');

    return {
      numeric: false,
      outlineId: fromId, // == toId by construction
      fromOrder: +fromOrd,
      toOrder: +toOrd,
      fromKey: +from.nodeKey.split('-').pop()!,
      toKey: +to.nodeKey.split('-').pop()!,
      original: ann,
    };
  }

  // No other formats are accepted
  return null;
};

/**
 * Build spatial index for fast annotation lookups by node coordinates
 */
const buildAnnotationSpatialIndex = (
  counterpoints: ProcessedCounterpoint[]
): AnnotationSpatialIndex => {
  const index: AnnotationSpatialIndex = {
    numeric: new Map(),
    outline: new Map(),
  };

  for (const cp of counterpoints) {
    for (const ann of cp.annotations) {
      if (ann.numeric) {
        // Index numeric annotations by order range
        for (let order = ann.fromOrder; order <= ann.toOrder; order++) {
          if (!index.numeric.has(order)) {
            index.numeric.set(order, []);
          }
          index.numeric.get(order)!.push(ann);
        }
      } else if (ann.outlineId) {
        // Index outline annotations by outlineId -> order range
        if (!index.outline.has(ann.outlineId)) {
          index.outline.set(ann.outlineId, new Map());
        }
        const outlineMap = index.outline.get(ann.outlineId)!;
        for (let order = ann.fromOrder; order <= ann.toOrder; order++) {
          if (!outlineMap.has(order)) {
            outlineMap.set(order, []);
          }
          outlineMap.get(order)!.push(ann);
        }
      }
    }
  }

  return index;
};

/**
 * Pre-processes counterpoint rounds for easier processing
 * Returns both processed counterpoints and a spatial index for O(1) lookups
 */
export const prepCounterpoints = (rounds: DebateRound[] = []): PreparedCounterpoints => {
  const counterpoints = rounds.map(cp => ({
    _id: cp._id,
    author: cp.author,
    team: cp.team,
    annotations: (cp.annotations || [])
      .filter(a => !a?.refAnnotation?._id) // keep only root annotations
      .map(prepAnnotation)
      .filter((a): a is ProcessedAnnotation => a !== null), // filter out null results
  }));

  const spatialIndex = buildAnnotationSpatialIndex(counterpoints);

  return { counterpoints, spatialIndex };
};

/**
 * Pre-processes text nodes for easier processing
 */
export const prepNodes = (nodes: TextNode[] = []): ProcessedTextNode[] =>
  nodes.map(n => {
    const numeric = typeof n.nodeOrder === 'number';
    if (numeric) {
      return {
        ...n,
        numeric,
        order: n.nodeOrder as number,
        keyNum: +n.nodeKey.split('-')[1],
      };
    }
    const [outlineId, ord] = (n.nodeOrder as string).split('-');
    return {
      ...n,
      numeric,
      outlineId,
      order: +ord,
      keyNum: +n.nodeKey.split('-').pop()!,
    };
  });

/**
 * Match annotations to a node using spatial index for O(1) lookup
 * Returns matched annotations organized by counterpoint
 */
export const matchAnnotationsToNode = (
  node: ProcessedTextNode,
  counterpoints: ProcessedCounterpoint[],
  spatialIndex: AnnotationSpatialIndex
): { annIDs: string[]; nodeCPs: any[] } => {
  const annIDs: string[] = [];
  const nodeCPs: any[] = [];
  const cpMap = new Map<string, any>();

  // Get candidate annotations from spatial index
  let candidates: ProcessedAnnotation[] = [];
  if (node.numeric) {
    candidates = spatialIndex.numeric.get(node.order) || [];
  } else if (node.outlineId) {
    const outlineMap = spatialIndex.outline.get(node.outlineId);
    candidates = outlineMap?.get(node.order) || [];
  }

  // Check each candidate annotation for full match (including keyNum range)
  for (const ann of candidates) {
    if (node.numeric !== ann.numeric) continue;

    let isMatch = false;
    if (ann.numeric) {
      if (node.keyNum >= ann.fromKey && node.keyNum <= ann.toKey) {
        isMatch = true;
      }
    } else {
      if (
        node.outlineId === ann.outlineId &&
        node.keyNum >= ann.fromKey &&
        node.keyNum <= ann.toKey
      ) {
        isMatch = true;
      }
    }

    if (isMatch) {
      // Find the counterpoint this annotation belongs to
      const cp = counterpoints.find(c => c.annotations.includes(ann));
      if (cp) {
        if (!cpMap.has(cp._id)) {
          cpMap.set(cp._id, {
            _id: cp._id,
            author: cp.author,
            team: cp.team,
            annotations: [],
          });
        }
        cpMap.get(cp._id)!.annotations.push(ann.original);
        annIDs.push(ann.original._id);
      }
    }
  }

  // Convert map to array
  cpMap.forEach(cp => {
    nodeCPs.push(cp);
  });

  return { annIDs, nodeCPs };
};
